import { ActiveElement, Chart, ChartOptions } from 'chart.js';
import { EChartTooltips, getChartTooltip } from 'src/constants/chartTooltips';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  findIfPrintDeviceProfile,
  findIfShowChartFour,
} from 'src/components/_charts/ChartDeviceProfile/helpers';

import { AxisXCustom } from 'src/components/_charts/AxisXCustom/AxisXCustom';
import { ChartDataLoadingWrapper } from 'src/components/ChartDataLoadingWrapper';
import { ChartWrapper } from 'src/components/ChartWrapper';
import { EChartName } from 'src/components/_charts/chartsData';
import { TChartDeviceProfileProps } from './ChartDeviceProfile.types';
import { commonInteraction } from 'src/components/_charts/chartsData/commonOptions';
import { config } from 'src/components/_charts/ChartDeviceProfile/config';
import { formatter } from 'src/utils/formatter';
import s from './ChartDeviceProfile.module.scss';
import { selectSettingsData } from 'src/redux/configuration/configuration.selectors';
import { useChartJS } from 'src/hooks/useChartJS';
import { useCurrentRefs } from 'src/hooks/useCurrentRefs';
import useDeviceStatisticsData from 'src/components/_charts/ChartDeviceProfile/useDeviceStatisticsData';
import { useSelector } from 'react-redux';
import vars from 'src/assets/styles/utils/vars.module.scss';

export const ChartDeviceProfile: React.FC<TChartDeviceProfileProps> = ({
  deviceStatics,
  startUnix,
  currentMarket,
  deviceType,
  forTimestamps,
}) => {
  const parsedData = useDeviceStatisticsData({ data: deviceStatics, deviceType, forTimestamps });
  const height = 115;
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const canvasOneRef = useRef<HTMLCanvasElement | null>(null);
  const chartOneRef = useRef<Chart | null>(null);
  const canvasTwoRef = useRef<HTMLCanvasElement | null>(null);
  const chartTwoRef = useRef<Chart | null>(null);
  const canvasThreeRef = useRef<HTMLCanvasElement | null>(null);
  const chartThreeRef = useRef<Chart | null>(null);
  const canvasFourRef = useRef<HTMLCanvasElement | null>(null);
  const chartFourRef = useRef<Chart | null>(null);
  const allChartsRef = useRef([chartOneRef, chartTwoRef, chartThreeRef, chartFourRef]);
  const { currency } = useSelector(selectSettingsData);
  const showChartFour = findIfShowChartFour(deviceType);
  const currentRefs = useCurrentRefs({ forTimestamps });

  const datasetsOne = useMemo(() => {
    return [
      {
        data: parsedData?.chartOneData || [],
        borderColor: vars['color-oh-so-green'],
        borderWidth: 1,
      },
    ];
  }, [parsedData?.chartOneData]);

  const datasetsTwo = useMemo(() => {
    const output = [
      {
        data: parsedData?.chartTwoData.energy || [],
        backgroundColor: vars['color-oh-so-green'],
      },
    ];

    if (findIfPrintDeviceProfile(deviceType)) {
      output.push({
        data: parsedData?.chartTwoData.deviceProfile || [],
        backgroundColor: vars['color-singularity-red'],
      });
    }

    return output;
  }, [deviceType, parsedData?.chartTwoData.deviceProfile, parsedData?.chartTwoData.energy]);

  const datasetsFour = useMemo(() => {
    const output = [
      {
        data: parsedData?.chartFourData.energy || [],
        backgroundColor: vars['color-oh-so-green'],
      },
    ];

    if (findIfPrintDeviceProfile(deviceType)) {
      output.push({
        data: parsedData?.chartFourData.deviceProfile || [],
        backgroundColor: vars['color-singularity-red'],
      });
    }

    return output;
  }, [deviceType, parsedData?.chartFourData.deviceProfile, parsedData?.chartFourData.energy]);

  const datasetsThree = useMemo(() => {
    return [
      {
        data: parsedData?.chartThreeData.data || [],
        backgroundColor: '#1A48ED',
        minBarLength: 2,
      },
    ];
  }, [parsedData?.chartThreeData]);

  const chartOptions: ChartOptions = useMemo(() => {
    return {
      onHover(context, t, chart) {
        if (!chart || typeof context.x !== 'number') {
          if (cursorRef.current) {
            cursorRef.current.style.opacity = '0';
          }

          return;
        }

        if (cursorRef.current) {
          Object.assign(cursorRef.current.style, {
            transform: `translateX(${context.x}px`,
            opacity: '1',
          });
        }

        // @ts-ignore
        const activeElements: ActiveElement[] = chart.tooltip?.getActiveElements?.();
        let index = 0;

        if (activeElements.length) {
          index = activeElements[0].index;
        } else {
          index = +(
            ((context.x - chart.chartArea.left) * currentRefs.current.forTimestamps.length) /
            // @ts-ignore
            chart.chartArea.width
          ).toFixed(0);
        }

        allChartsRef.current.forEach((ref) => {
          const chart = ref.current;
          if (!chart) return;

          const newActiveElements = chart.data.datasets.reduce((acc, dataset, datasetIndex) => {
            if (index in dataset.data) {
              acc.push({
                datasetIndex,
                index,
              });
            }

            return acc;
          }, [] as Array<{ datasetIndex: number; index: number }>);

          if (newActiveElements.length) {
            // @ts-ignore
            chart.tooltip.setActiveElements?.(newActiveElements);

            chart.update();
          }
        });
      },
    };
  }, [currentRefs]);

  useChartJS(
    EChartName.DeviceProfileOne,
    canvasOneRef,
    chartOneRef,
    {
      datasets: datasetsOne,
    },
    { chartOptions },
  );

  useChartJS(
    EChartName.DeviceProfileTwo,
    canvasTwoRef,
    chartTwoRef,
    {
      datasets: datasetsTwo,
    },
    { chartOptions, datasetsLengthWillChange: true },
  );

  useChartJS(
    EChartName.DeviceProfileThree,
    canvasThreeRef,
    chartThreeRef,
    {
      labels: forTimestamps,
      // @ts-ignore
      datasets: datasetsThree,
    },
    { chartOptions },
  );

  useChartJS(
    EChartName.DeviceProfileFour,
    canvasFourRef,
    chartFourRef,
    {
      datasets: datasetsFour,
    },
    { chartOptions, datasetsLengthWillChange: true },
  );

  // Controls tooltip/axis labels
  useEffect(() => {
    if (chartOneRef.current) {
      chartOneRef.current.config.data.datasets[0].label = JSON.stringify({
        title: config.tooltipLabels[deviceType][0],
        unit: config.tooltipUnits[deviceType][0],
      });
      // @ts-ignore
      chartOneRef.current.options.scales!.y!.title!.text = config.YAxis[deviceType].label[0];

      chartOneRef.current.update();
    }

    if (chartTwoRef.current) {
      const [energyDataset, deviceProfileDataset] = chartTwoRef.current.config.data.datasets;

      energyDataset.label = JSON.stringify({
        title: config.tooltipLabels[deviceType][1],
        unit: config.tooltipUnits[deviceType][1],
      });

      if (deviceProfileDataset) {
        deviceProfileDataset.label = JSON.stringify({
          title: config.tooltipLabels[deviceType][0],
          unit: config.tooltipUnits[deviceType][0],
        });
      }
      // @ts-ignore
      chartTwoRef.current.options.scales!.y!.title!.text = config.YAxis[deviceType].label[1];

      chartTwoRef.current.update();
    }

    if (chartFourRef.current) {
      const [energyDataset, deviceProfileDataset] = chartFourRef.current.config.data.datasets;

      energyDataset.label = JSON.stringify({
        title: config.tooltipLabels[deviceType][3],
        unit: config.tooltipUnits[deviceType][3],
      });

      if (deviceProfileDataset) {
        deviceProfileDataset.label = JSON.stringify({
          title: config.tooltipLabels[deviceType][1],
          unit: config.tooltipUnits[deviceType][1],
        });
      }
      // @ts-ignore
      chartFourRef.current.options.scales!.y!.title!.text = config.YAxis[deviceType].label[3];

      chartFourRef.current.update();
    }

    if (chartThreeRef.current) {
      const unit = `${formatter.getCurrencySymbol(currency)}/kWh`;
      chartThreeRef.current.config.data.datasets[0].label = JSON.stringify({
        title: ['Min', 'Max'],
        unit: [unit, unit],
      });
      // @ts-ignore
      chartThreeRef.current.options.scales!.y!.title!.text = config.YAxis[deviceType].label[2];

      if (parsedData?.chartThreeData.domain) {
        const [min, max] = parsedData.chartThreeData.domain;
        if (typeof min === 'number' && typeof max === 'number') {
          // Sometimes bars are not plotted, changing domain for yAxis fixes this
          chartThreeRef.current.options.scales!.y!.min = min * 1.5;
          chartThreeRef.current.options.scales!.y!.max = max * 1.5;
        }
      }

      chartThreeRef.current.update();
    }
  }, [currency, deviceType, parsedData?.chartThreeData.domain]);

  const AxisX = <AxisXCustom className={s.axisX} startUnix={startUnix} endUnix={currentMarket} />;

  return (
    <ChartWrapper
      title="Profile Over Time"
      info={getChartTooltip(
        EChartTooltips[`DEVICE_PROFILE_${deviceType}`],
        formatter.getCurrencySymbol(currency),
      )}>
      <ChartDataLoadingWrapper loading={!deviceStatics}>
        {AxisX}

        <div className={s.chartsWrapper}>
          <div
            className={s.chartsInnerWrapper}
            onMouseLeave={() => {
              allChartsRef.current.forEach((item) => {
                const chart = item.current;

                setTimeout(() => {
                  if (!chart) return;
                  // @ts-ignore
                  chart.tooltip.setActiveElements?.([]);

                  if (cursorRef.current) {
                    cursorRef.current.style.opacity = '0';
                  }

                  chart.update();
                }, 50);
              });
            }}>
            <div className={s.cursor} ref={cursorRef} />

            <canvas
              ref={canvasOneRef}
              height={height}
              style={{
                background: vars['color-athens-gray'],
                display: showChartFour ? 'none' : undefined,
              }}
            />

            <canvas
              ref={canvasTwoRef}
              height={height}
              style={{
                background: showChartFour ? vars['color-athens-gray'] : undefined,
              }}
            />

            <canvas
              ref={canvasFourRef}
              height={height}
              style={{ display: !showChartFour ? 'none' : undefined }}
            />

            <canvas
              ref={canvasThreeRef}
              height={height}
              style={{
                background: vars['color-athens-gray'],
                display:
                  deviceType === 'Storage' && parsedData?.chartThreeData.noData
                    ? 'none'
                    : undefined,
              }}
            />
          </div>
        </div>

        {AxisX}
      </ChartDataLoadingWrapper>
    </ChartWrapper>
  );
};
